import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { getUserThemeContrastColor } from '../theme/colors';

import { ReactComponent as BarChart } from './svg/chart.svg';
import { ReactComponent as Books } from './svg/book.svg';
import { ReactComponent as BulletedList } from './svg/bulletlist.svg';
import { ReactComponent as Calendar } from './svg/calendar.svg';
import { ReactComponent as Diploma } from './svg/diploma.svg';
import { ReactComponent as Check } from './svg/check.svg';
import { ReactComponent as Download } from './svg/download.svg';
import { ReactComponent as File } from './svg/file.svg';
import { ReactComponent as Fire } from './svg/fire.svg';
import { ReactComponent as Flash } from './svg/flash.svg';
import { ReactComponent as Food } from './svg/food.svg';
import { ReactComponent as Group } from './svg/group.svg';
import { ReactComponent as Claps } from './svg/handclaps.svg';
import { ReactComponent as Health } from './svg/health.svg';
import { ReactComponent as Idea } from './svg/idea.svg';
import { ReactComponent as Image } from './svg/image.svg';
import { ReactComponent as Invitation } from './svg/invitations.svg';
import { ReactComponent as Kpi } from './svg/kpi.svg';
import { ReactComponent as Money } from './svg/money.svg';
import { ReactComponent as Nature } from './svg/nature.svg';
import { ReactComponent as Notepad } from './svg/note.svg';
import { ReactComponent as Online } from './svg/online.svg';
import { ReactComponent as Pen } from './svg/pen.svg';
import { ReactComponent as Profile } from './svg/profile.svg';
import { ReactComponent as Professor } from './svg/teacher.svg';
import { ReactComponent as ProsCons } from './svg/proscons.svg';
import { ReactComponent as Puzzle } from './svg/puzzle.svg';
import { ReactComponent as Questions } from './svg/questions.svg';
import { ReactComponent as Settings } from './svg/settingw.svg';
import { ReactComponent as SpellCheck } from './svg/spellcheck.svg';
import { ReactComponent as Star } from './svg/star.svg';
import { ReactComponent as StepByStep } from './svg/stepbystep.svg';
import { ReactComponent as Student } from './svg/student.svg';
import { ReactComponent as Repeat } from './svg/repeat.svg';
import { ReactComponent as Clock } from './svg/time.svg';
import { ReactComponent as Tree } from './svg/tree.svg';
import { ReactComponent as Video } from './svg/video.svg';
import { ReactComponent as Certified } from './svg/certified.svg';
import { ReactComponent as Appartment } from './svg/appartment.svg';
import { ReactComponent as Baking } from './svg/baking.svg';
import { ReactComponent as Bidding } from './svg/bidding.svg';
import { ReactComponent as Chef } from './svg/chef.svg';
import { ReactComponent as Contract } from './svg/contract.svg';
import { ReactComponent as Cooking } from './svg/cooking.svg';
import { ReactComponent as Curtains } from './svg/curtains.svg';
import { ReactComponent as Gift } from './svg/gift.svg';
import { ReactComponent as House } from './svg/house.svg';
import { ReactComponent as Sofa } from './svg/sofa.svg';

// Deprecated (eventually remove it):
import { ReactComponent as ClockForward } from './svg/Clock-Forward.svg';
import { ReactComponent as FilePictures } from './svg/File-Pictures.svg';
import { ReactComponent as Lections } from './svg/Lections.svg';
import { ReactComponent as LetterOpen } from './svg/Letter-Open.svg';
import { ReactComponent as Statistic } from './svg/Statistic.svg';
import { ReactComponent as StudentHat } from './svg/Student-Hat.svg';
import { ReactComponent as TextHighlight } from './svg/Text-HighlightColor.svg';
import { ReactComponent as User } from './svg/User.svg';
import { ReactComponent as VideoFour } from './svg/Video-4.svg';
import { ReactComponent as VideoFive } from './svg/Video-5.svg';

/**
 * Size configuration for symbols
 */
const symbolSize = { 
  'x-small': '30px', 
  'small': '40px', 
  'medium': '50px',
  'large': '70px',
  'gigantic': '100px'
};

/**
 * Styled wrapper for SVG components
 */
const SvgWrapper = styled.i`
  display: inline-block;
  width: ${({ size }) => symbolSize[size] || symbolSize.medium};
  height: ${({ size }) => symbolSize[size] || symbolSize.medium};
  align-items: center;
  justify-content: center;
  background-color: ${({ $withBgColor }) => $withBgColor || 'transparent'};
  border-radius: ${({ size }) => symbolSize[size] || symbolSize.medium};

  svg {
    width: ${({ size }) => symbolSize[size] || symbolSize['x-small']};
    height: ${({ size }) => `calc(${symbolSize[size] || symbolSize['x-small']} - 4px)`};
    fill: ${({ color, $withBgColor }) => !color && $withBgColor ? getUserThemeContrastColor($withBgColor) : color};
    padding-top:4px;
  }
  
  svg path {
    stroke: ${({ color, $withBgColor=true }) => !color && $withBgColor ? getUserThemeContrastColor($withBgColor) : color};
    fill: ${({ color, $usefill, $withBgColor=true }) => $usefill && (!color && $withBgColor) ? getUserThemeContrastColor($withBgColor) : color};
  }
`;

/**
 * Creates a standardized symbol component using the SVG React component
 */
const createSymbol = (Component, usefill = true) => {
  // If no component is provided, return null
  if (!Component) return null;
  
  const SymbolComponent = ({ size = 'medium', color, withBgColor, ...rest }) => (
    <SvgWrapper 
      size={size} 
      color={color} 
      $withBgColor={withBgColor} 
      $usefill={usefill}
      {...rest}
    >
      <Component />
    </SvgWrapper>
  );
  
  SymbolComponent.propTypes = {
    size: PropTypes.oneOf(['x-small', 'small', 'medium', 'large', 'gigantic']),
    color: PropTypes.string,
    withBgColor: PropTypes.string
  };
  
  return SymbolComponent;
};

/**
 * Special wrapper for Material-UI components to maintain consistent styling
 */
const MuiWrapper = styled.div`
  width: ${({ size }) => symbolSize[size] || symbolSize.medium};
  height: ${({ size }) => symbolSize[size] || symbolSize.medium};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $withBgColor }) => $withBgColor || 'transparent'};
  border-radius: ${({ size }) => symbolSize[size] || symbolSize.medium};

  & > * {
    width: 80%;
    height: 80%;
    color: ${({ color, $withBgColor }) => !color && $withBgColor ? getUserThemeContrastColor($withBgColor) : color};
  }
`;

/**
 * Creates a standardized symbol component using a Material-UI component
 */
const createMuiSymbol = (Component) => {
  if (!Component) return null;
  
  const MuiSymbolComponent = ({ size = 'medium', color, withBgColor, ...rest }) => (
    <MuiWrapper 
      size={size} 
      color={color} 
      $withBgColor={withBgColor} 
      {...rest}
    >
      <Component />
    </MuiWrapper>
  );
  
  MuiSymbolComponent.propTypes = {
    size: PropTypes.oneOf(['x-small', 'small', 'medium', 'large', 'gigantic']),
    color: PropTypes.string,
    withBgColor: PropTypes.string
  };
  
  return MuiSymbolComponent;
};

/**
 * Main collection of all available symbols
 */
const Symbols = {
  // Main symbols
  BarChart: createSymbol(BarChart),
  Books: createSymbol(Books),
  BulletedList: createSymbol(BulletedList),
  Calendar: createSymbol(Calendar),
  Claps: createSymbol(Claps),
  Clock: createSymbol(Clock),
  Check: createSymbol(Check),
  Diploma: createSymbol(Diploma),
  Download: createSymbol(Download),
  File: createSymbol(File),
  Fire: createSymbol(Fire),
  Flash: createSymbol(Flash),
  Food: createSymbol(Food),
  Group: createSymbol(Group),
  Health: createSymbol(Health),
  Idea: createSymbol(Idea),
  Image: createSymbol(Image),
  Kpi: createSymbol(Kpi),
  Money: createSymbol(Money),
  Nature: createSymbol(Nature),
  Notepad: createSymbol(Notepad),
  Pen: createSymbol(Pen),
  Professor: createSymbol(Professor),
  Profile: createSymbol(Profile),
  ProsCons: createSymbol(ProsCons),
  Puzzle: createSymbol(Puzzle),
  Questions: createSymbol(Questions),
  Settings: createSymbol(Settings),
  Online: createSymbol(Online),
  SpellCheck: createSymbol(SpellCheck),
  Star: createSymbol(Star),
  StepByStep: createSymbol(StepByStep),
  Student: createSymbol(Student),
  Tree: createSymbol(Tree),
  Video: createSymbol(Video),
  
  // Special MUI symbols
  Invitation: createSymbol(Invitation),
  Upload: createMuiSymbol(CloudUpload),
  Loading: createMuiSymbol(CircularProgress),
  
  // Home & Lifestyle symbols
  Certified: createSymbol(Certified),
  Appartment: createSymbol(Appartment),
  Baking: createSymbol(Baking),
  Bidding: createSymbol(Bidding),
  Chef: createSymbol(Chef),
  Contract: createSymbol(Contract),
  Cooking: createSymbol(Cooking),
  Curtains: createSymbol(Curtains),
  Gift: createSymbol(Gift),
  House: createSymbol(House),
  Sofa: createSymbol(Sofa),

  // Deprecated symbols
  ClockForward: createSymbol(ClockForward),
  FilePictures: createSymbol(FilePictures),
  Lections: createSymbol(Lections, false),
  LetterOpen: createSymbol(LetterOpen),
  Repeat: createSymbol(Repeat),
  Statistic: createSymbol(Statistic),
  StudentHat: createSymbol(StudentHat),
  TextHighlight: createSymbol(TextHighlight),
  User: createSymbol(User),
  VideoFour: createSymbol(VideoFour),
  VideoFive: createSymbol(VideoFive),
};

// Export list of symbol names
export const SYMBOL_NAMES = Object.keys(Symbols).filter(key => !!Symbols[key]);

// Export categories for documentation and component display
export const SYMBOL_CATEGORIES = {
  main: [
    'BarChart', 'Books', 'BulletedList', 'Calendar', 'Claps', 
    'Clock', 'Check', 'Diploma', 'Download', 'File',
    'Fire', 'Flash', 'Food', 'Group', 'Health',
    'Idea', 'Image', 'Kpi', 'Money', 'Nature',
    'Notepad', 'Pen', 'Professor', 'Profile', 'ProsCons',
    'Puzzle', 'Questions', 'Settings', 'Online', 'SpellCheck',
    'Star', 'StepByStep', 'Student', 'Tree', 'Video'
  ].filter(name => !!Symbols[name]),
  
  special: [
    'Invitation', 'Upload', 'Loading'
  ].filter(name => !!Symbols[name]),
  
  homeLifestyle: [
    'Certified', 'Appartment', 'Baking', 'Bidding', 'Chef', 
    'Contract', 'Cooking', 'Curtains', 'Gift', 'House', 'Sofa'
  ].filter(name => !!Symbols[name]),
  
  deprecated: [
    'ClockForward', 'FilePictures', 'Lections', 'LetterOpen', 'Repeat',
    'Statistic', 'StudentHat', 'TextHighlight', 'User', 'VideoFour', 'VideoFive'
  ].filter(name => !!Symbols[name])
};

export default Symbols;
